import React, { useState, useEffect } from 'react';
import _, { compact, find } from 'lodash';
import HelloSign from 'hellosign-embedded';
import {
  Flex, Text, FormTag, Loading, Box,
} from '../components';
import { stateNamesAndForms, stateOptions } from './commonStateTaxData';
import Axios from '../axios';
import {
  skipDomainVerificationHelloSign,
  helloSignClientId,
} from '../../../config';
import AlabamaForm from './alabama/AlabamaForm';
import ArizonaForm from './arizona/ArizonaForm';
import ArkansasForm from './arkansas/ArkansasForm';
import CaliforniaForm from './california/CaliforniaForm';
import ConnecticutForm from './connecticut/ConnecticutForm';
import DCForm from './dc/DCForm';
import GeorgiaForm from './georgia/GeorgiaForm';
import HawaiiForm from './hawaii/hawaii-form';
import IdahoForm from './idaho/IdahoForm';
import IllinoisForm from './illinois/IllinoisForm';
import IndianaForm from './indiana/IndianaForm';
import IowaForm from './iowa/iowa-form';
import KansasForm from './kansas/KansasForm';
import KentuckyForm from './kentucky/KentuckyForm';
import LouisianaForm from './louisiana/LouisianaForm';
import MaineForm from './maine/MaineForm';
import MarylandForm from './maryland/MarylandForm';
import MassachusettsForm from './massachusetts/MassachusettsForm';
import MichiganForm from './michigan/michigan-form';
import MinnesotaForm from './minnesota/MinnesotaForm';
import MississippiForm from './mississippi/MississippiForm';
import MissouriForm from './missouri/missouri-form';
import MontanaForm from './montana/montana-form';
import NebraskaForm from './nebraska/nebraska-form';
import NewYorkForm from './new-york/NewYorkForm';
import NorthCarolinaForm from './north-carolina/NorthCarolinaForm';
import NewJerseyForm from './new-jersey/NewJerseyForm';
import OhioForm from './ohio/OhioForm';
import OklahomaForm from './oklahoma/OklahomaForm';
import OregonForm from './oregon/OregonForm';
import RhodeIslandForm from './rhode-island/RhodeIslandForm';
import SouthCarolinaForm from './south-carolina/SouthCarolinaForm';
import StateW4Form from './state-w4/StateW4Form';
import VermontForm from './vermont/vermont-form';
import VirginiaForm from './virginia/VirginiaForm';
import WestVirginiaForm from './west-virginia/WestVirginiaForm';
import WisconsinForm from './wisconsin/wisconsin-form';
import PennsylvaniaForm from './pennsylvania/pennsylvania-form';
import DelawareForm from './delaware/delaware-form';

import { PageTitle } from '../biz-components/page-title';

const componentNames = {
  AL: AlabamaForm,
  AR: ArkansasForm,
  AZ: ArizonaForm,
  CA: CaliforniaForm,
  CO: StateW4Form,
  CT: ConnecticutForm,
  DC: DCForm,
  DE: DelawareForm,
  GA: GeorgiaForm,
  HI: HawaiiForm,
  IA: IowaForm,
  ID: IdahoForm,
  IL: IllinoisForm,
  IN: IndianaForm,
  KS: KansasForm,
  KY: KentuckyForm,
  LA: LouisianaForm,
  MA: MassachusettsForm,
  MD: MarylandForm,
  ME: MaineForm,
  MI: MichiganForm,
  MN: MinnesotaForm,
  MO: MissouriForm,
  MS: MississippiForm,
  MT: MontanaForm,
  NC: NorthCarolinaForm,
  ND: StateW4Form,
  NE: NebraskaForm,
  NJ: NewJerseyForm,
  NM: StateW4Form,
  NY: NewYorkForm,
  OH: OhioForm,
  OR: OregonForm,
  OK: OklahomaForm,
  RI: RhodeIslandForm,
  SC: SouthCarolinaForm,
  UT: StateW4Form,
  VT: VermontForm,
  VA: VirginiaForm,
  WI: WisconsinForm,
  WV: WestVirginiaForm,
  PA: PennsylvaniaForm,
};

function EmployeeFillForm({
  action,
  companyStaff,
  prefilledData = {},
  dataFromEi = {},
  onSignedSuccess,
  viewResultAsManager,
}) {
  const localIsOnboardingV2 = isOnboardingV2 === 'true';
  const [saveDataError, setSaveDataError] = useState('');
  const [signError, setSignError] = useState('');
  const [signLink, setSignLink] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [usStateCode, setUsStateCode] = useState('');
  const [formSubmissionDigestKey, setFormSubmissionDigestKey] = useState('');
  const [data, setData] = useState(null);
  const [version, setVersion] = useState('');

  useEffect(() => {
    const actionDigestKey = action.digest_key;
    const apiURL = `${APIHOST}/v1/onboarding/us_state_tax/employee/employee_onboarding_actions/:employee_onboarding_action_digest_key/us_state_tax_form_submission`.replace(
      ':employee_onboarding_action_digest_key',
      actionDigestKey,
    );
    setIsLoading(true);

    const config = { headers: { Authorization: authCode } };

    Axios.get(apiURL, config)
      .then((res) => {
        setUsStateCode(res.data.us_state_tax_form_submission.us_state_code);
        setFormSubmissionDigestKey(
          res.data.us_state_tax_form_submission.digest_key,
        );
        setData(res.data.us_state_tax_form_submission.data);
        setVersion(res.data.us_state_tax_form_submission.version);
      })
      .then(() => {
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    const helloSignClient = initHelloSign();

    return () => {
      helloSignClient.close();
    };
  }, [signLink]);

  const getPdfUrl = () => `${APIHOST}/v1/onboarding/us_state_tax/hr/us_state_tax_form_submissions/:digest_key/pdf`.replace(
    ':digest_key',
    formSubmissionDigestKey,
  );

  const formatFullName = () => {
    const firstName = prefilledData.employee_first_name || dataFromEi.employee_first_name || '';
    const middleInitial = prefilledData.employee_middle_name || dataFromEi.employee_middle_name || '';
    const lastName = prefilledData.employee_last_name || dataFromEi.employee_last_name || '';
    return (
      `${firstName} ` + `${middleInitial ? `${middleInitial} ` : ''}${lastName}`
    );
  };

  const formatFirstNameMiddleInitial = () => {
    const firstName = prefilledData.employee_first_name || dataFromEi.employee_first_name || '';
    const middleInitial = prefilledData.employee_middle_name || dataFromEi.employee_middle_name || '';
    return `${firstName}${middleInitial ? ` ${middleInitial}` : ''}`;
  };

  const getInitialQuestions = (questions, initialValue = '') => questions.map((question) => {
    const value = data[question.id] || prefilledData[question.id] || dataFromEi[question.id] || initialValue;
    const answer = {
      answer: {
        [question.id]: value,
      },
    };
    return {
      ...question,
      answer,
    };
  });

  const resetInitialQuestions = (questions, initialValue = '') => questions.map((question) => {
    const value = prefilledData[question.id] || dataFromEi[question.id] || initialValue;
    const answer = {
      answer: {
        [question.id]: value,
      },
    };
    return {
      ...question,
      answer,
    };
  });

  const scrollTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const saveFormData = (data, successCallback) => {
    setSaveDataError('');
    setIsSubmitting(true);
    const path = `${APIHOST}/v1/onboarding/us_state_tax/employee/us_state_tax_form_submissions/${formSubmissionDigestKey}`;
    Axios.put(path, { data })
      .then((responseData) => {
        setIsSubmitting(false);
        successCallback(responseData);
      })
      .catch((error) => {
        let dataError = 'Something went wrong while saving data.';
        if (error && error.response && error.response.data) {
          const { message } = error.response.data;
          if (message) {
            dataError = message;
          }
        }
        setSaveDataError(dataError);
        setIsSubmitting(false);
      });
  };

  const handleSigning = () => {
    const path = `${APIHOST}/v1/onboarding/us_state_tax/employee/us_state_tax_form_submissions/${formSubmissionDigestKey}/sign`;
    setIsSubmitting(true);
    Axios.post(path)
      .then((responseData) => {
        setIsSubmitting(false);
        const { sign_url: link } = responseData.data.us_state_tax_form_submission;
        setSignLink(link);
      })
      .catch(() => {
        setIsSubmitting(false);
      });
  };

  const initHelloSign = () => {
    setSignError('');
    const containerId = 'iframe-container';
    const hellosignClient = new HelloSign({
      clientId: helloSignClientId,
    });

    hellosignClient.once(HelloSign.events.SIGN, (eventData) => {
      const { event } = eventData;
      if (event === HelloSign.EVENT_SIGNED) {
        // Call api to mark this document as signed
        const path = `${APIHOST}/v1/onboarding/us_state_tax/employee/us_state_tax_form_submissions/:digest_key/signed`.replace(
          ':digest_key',
          formSubmissionDigestKey,
        );
        Axios.post(path, { sign_url: signLink })
          .then(() => {
            onSignedSuccess();
          })
          .catch((error) => {
            let signError = 'Something wrong with the signing.';
            if (error && error.response && error.response.data) {
              const { message } = error.response.data;
              if (message) {
                signError = message;
              }
            }
            setSignError(signError);
          });
      }
    });

    const options = {
      skipDomainVerification: skipDomainVerificationHelloSign,
      container: document.getElementById(containerId),
    };

    if (signLink) {
      hellosignClient.open(signLink, options);
    }

    return hellosignClient;
  };

  const markActionAsCompleted = () => {
    const actionDigestKey = action.digest_key;
    const apiURL = `${APIHOST}/v1/onboarding/employee/employee_onboarding_actions/${actionDigestKey}/completed`;
    const config = {
      method: 'PUT',
      url: apiURL,
      headers: {
        ...(authCode && { Authorization: authCode }),
      },
    };
    setIsSubmitting(true);
    Axios(config)
      .then(() => {
        setIsSubmitting(false);
        window.location.reload();
      })
      .catch(() => {
        setIsSubmitting(false);
      });
  };

  const renderFormHeading = () => {
    const formName = stateNamesAndForms[usStateCode].form;
    const linkUpdated = `link${version}`;
    const formLink = stateNamesAndForms[usStateCode][linkUpdated]
      || stateNamesAndForms[usStateCode].link;

    return (
      <Flex style={{ paddingBottom: '20px' }} column>
        <a
          target="_blank"
          rel="noopener noreferrer"
          style={{
            lineHeight: '20px',
            textDecoration: 'none',
            color: '#3F51B5',
          }}
          href={formLink}
        >
          <Box width="fit-content" marginBottom="8px">
            <Flex
              alignItems="center"
              backgroundColor="#F4F4F5"
              padding="6px 8px"
            >
              <Text fontSize={12} color="#787883" fontWeight="600">
                {formName}
              </Text>
              <img
                src="/j/images/external-link-black.svg"
                style={{ marginLeft: 6 }}
                width={12}
                height={12}
                alt="external link"
                color="black"
              />
            </Flex>
          </Box>
        </a>
      </Flex>
    );
  };

  const renderStateForm = () => {
    const StateTaxForm = componentNames[usStateCode];
    const linkUpdated = `link${version}`;
    const formLink = stateNamesAndForms[usStateCode][linkUpdated]
      || stateNamesAndForms[usStateCode].link;
    const props = {
      prefilledData,
      saveFormData,
      isSubmitting,
      scrollTop,
      handleSigning,
      viewResultAsManager,
      data,
      companyStaff,
      getPdfUrl,
      getInitialQuestions,
      resetInitialQuestions,
      formatFullName,
      formatFirstNameMiddleInitial,
      markActionAsCompleted,
      version,
      formLink,
      isOnboardingV2: localIsOnboardingV2,
    };

    return (
      <div>
        <StateTaxForm {...props} />
      </div>
    );
  };

  return (
    <Flex column>
      <Flex column style={localIsOnboardingV2 ? { margin: 'auto', width: '100%' } : { margin: 'auto' }}>
        {isLoading && (
          <Flex>
            <Loading className="spinner spinner-blue" />
          </Flex>
        )}
        {
          data && usStateCode && localIsOnboardingV2 && (
            <PageTitle title="Fill state tax form" />
          )
        }
        {data && usStateCode && (
          <div>
            {renderFormHeading()}
            {saveDataError && (
              <Flex justifyContent="flex-end" mt={1}>
                <Text color="red">{saveDataError}</Text>
              </Flex>
            )}
            {signError && (
              <Flex justifyContent="flex-end" mt={1}>
                <Text color="red">{signError}</Text>
              </Flex>
            )}
          </div>
        )}
      </Flex>
      <Flex column style={{ width: '100%', margin: 'auto' }}>
        {data && usStateCode && <div>{renderStateForm()}</div>}
      </Flex>
    </Flex>
  );
}

export default EmployeeFillForm;
